import * as React from 'react';
import * as ReactModal from 'react-modal';

import Button from '../../components/buttons/Button';
import { TextArea } from '../../components/forms';

import JsonAnswerDetails from './JsonAnswerDetails';
import ActivitiesQuestionAnswer from '../customers/QASubComponents/ActivitiesQuestionAnswer';
import ReproductiveQuestionAnswer from '../customers/QASubComponents/ReproductiveQuestionAnswer';

import Utils from '../../common/utils';

interface Props {
	className?: string;
	children?: {};
	isOpen: boolean;
	onRequestClose: () => void;
	onNoteChange: (e: any) => void;
	onNoteSave: () => void;
	questionAnswerPair: ApplicationAnswerDto;
	questionAnswerPairNote: CustomerNote;
	questions: { [name: string]: string; }[];
	allowFormat: boolean;
	declinedQuestionNames: string[];
}

const ANIM_DURATION = 300;

class PolicyAnswerModal extends React.Component<Props> {
	constructor(props: Props) {
		super(props);

		// http://reactcommunity.org/react-modal/accessibility/#app-element
		ReactModal.setAppElement('#root');
	}

	componentDidMount() {
		if (this.props.isOpen) {
			Utils.disableBodyScrolling({allBreakpoints: true});
		}
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.isOpen !== this.props.isOpen) {
			if (this.props.isOpen) {
				Utils.disableBodyScrolling({allBreakpoints: true});
			}
			else {
				Utils.enableBodyScrolling();
			}
		}
	}

	componentWillUnmount() {
		Utils.enableBodyScrolling();
	}

	onAfterClose = () => {
		Utils.enableBodyScrolling();
	}

	render() {
		return (
			<ReactModal
				className="ReactModal_Content section section-main" // Redefine className to remove inline styles
				closeTimeoutMS={ANIM_DURATION}
				contentLabel="TODO"
				isOpen={this.props.isOpen}
				overlayClassName="ReactModal_Overlay" // Redefine className to remove inline styles
				portalClassName="ReactModal" // Redefine className to remove inline styles
				shouldCloseOnEsc={true}
				shouldCloseOnOverlayClick={true}
				onRequestClose={this.props.onRequestClose}
			>
				<h2 className="mb-3">
					Question {this.props.questionAnswerPair.categoryId}.{this.props.questionAnswerPair.subCategoryId}
				</h2>
				<dl>
					<dt>Question:</dt>
					<dd>{this.props.questionAnswerPair.questionValue}</dd>
					<dt>Answer:</dt>
					{this.renderComponentByQuestionName(this.props.questionAnswerPair)}
				</dl>
				<TextArea
					labelText="Notes"
					name="notes"
					value={this.props.questionAnswerPairNote.notes}
					onChange={this.props.onNoteChange}
				/>
				<div className="d-flex justify-content-end">
					<Button className="btn-secondary" onClick={this.props.onNoteSave}>
							Save Notes
					</Button>
				</div>
			</ReactModal>
		);
	}

	private renderComponentByQuestionName = (questionAnswerPair: ApplicationAnswerDto) => {
		if (this.props.allowFormat === false) {
			return (<dd>{questionAnswerPair.answer}</dd>);
		}
		const questionName = questionAnswerPair.questionName;
		const answer = questionAnswerPair.answer;
		switch (questionName) {
			case 'activities':
				return (
				<ActivitiesQuestionAnswer
					answer={answer!}
					declinedQuestionNames={this.props.declinedQuestionNames}
					questions={this.props.questions}
				/>);
			case 'abroad':
			case 'ownPetsTypes':
			case 'cancer':
			case 'heartCirculatory':
			case 'neurological':
			case 'respiratory':
			case 'liver':
			case 'blood':
			case 'immuno':
			case 'head':
			case 'psychological':
				return (
				<JsonAnswerDetails 
					answer={answer}
					questionName={questionName}
					declinedQuestionNames={this.props.declinedQuestionNames}
				/>);
			case 'reproductive':
				return (
				<ReproductiveQuestionAnswer
					answer={answer!}
					declinedQuestionNames={this.props.declinedQuestionNames}
					questions={this.props.questions}
				/>);
			case 'favoriteActivities':
				return (
					<ul className="mb-0">
						{JSON.parse(questionAnswerPair.answer!).map((item: string, index: number) => {
							return (<li key={index}>{item}</li>);
						})}
					</ul>);
			// code below is for fixing the inconsistency between value on UI with value saved in DB
			case 'specificJob':
				switch (answer) {
					case 'offshoreFisher':
						return (<dd>Offshore commercial fisher not coming ashore daily</dd>);
					case 'steelErector':
						return (<dd>Steel erector or scaffolder working at heights over 40 feet</dd>);
					case 'fallerOrLumberjack':
						return (<dd>Faller or lumberjack</dd>);
					case 'offshoreDriller':
						return (<dd>Offshore driller</dd>);
					case 'blaster':
						return (<dd>Blaster or explosive handler</dd>);
					case 'occupationNoneOfTheAbove':
						return (<dd>None of listing selections</dd>);
					default:
						return;
				}
			case 'reasonForUnemployment':
				switch (answer) {
					case 'parent':
						return (<dd>Parent or caregiver</dd>);
					case 'disabledOrMedicalLeave':
						return (<dd>On disability or medical leave</dd>);
					case 'socialAssistance':
						return (<dd>Social assistance</dd>);
					case 'cannotFindWork':
						return (<dd>Cannot find work</dd>);
					case 'retired':
						return (<dd>Retired</dd>);
					case 'other':
						return (<dd>Other reason</dd>);
					default:
						return;
				}
			case 'exerciseFrequentlyHours':
				switch (answer) {
					case '0':
						return (<dd>I rarely excercise</dd>);
					case '0-1':
						return (<dd>Less than 1 hour</dd>);
					case '1-2':
						return (<dd>1-2 hours</dd>);
					case '2-3':
						return (<dd>2-3 hours</dd>);
					case '3-4':
						return (<dd>3-4 hours</dd>);
					case '4-5':
						return (<dd>4-5 hours</dd>);
					case '5-6':
						return (<dd>5-6 hours</dd>);
					case '6-7':
						return (<dd>6-7 hours</dd>);
					case '7+':
						return (<dd>More than 7 hours</dd>);
					default:
						return;
				}
			case 'usedTobaccoLastUsed':
				switch (answer) {
					case 'withinThePastMonth':
						return (<dd>Within the past month</dd>);
					case 'withinThePast6Months':
						return (<dd>1-6 months ago</dd>);
					case 'withinThePast12Months':
						return (<dd>6-12 months ago</dd>);
					case 'withinThePast24Months':
						return (<dd>12-24 months ago</dd>);
					default:
						return;
				}
			default:
				return (<dd>{this.formatAnswer(answer, questionName!)}</dd>);
		}
	}

	private formatAnswer = (answer: any, questionName: string) => {
		if (questionName === 'consumedAlcohol' && answer !== 'false') {
			return answer;
		}
		if (typeof answer === 'string') {
			const splitExpression = new RegExp('(^|\s)(?=[A-Z])');
			const formattedAnswer = answer.split(splitExpression).join(' ');
			return formattedAnswer.charAt(0).toUpperCase() + formattedAnswer.slice(1);
		}
		return answer;
	}
}

export default PolicyAnswerModal;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/admin/common/PolicyAnswerModal.tsx